import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Copy, Check } from 'lucide-react'
import { useTextValidation } from '@/hooks/useTextValidation'
import type { CompiledFieldRules } from '@/hooks/useTextValidation'

interface EditableTitleProps {
  value: string
  original: string
  onChange: (value: string) => void
  rules: CompiledFieldRules | undefined
}

export function EditableTitle({ value, original, onChange, rules }: EditableTitleProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [copied, setCopied] = useState(false)
  const { violations, severity, charsUsed, maxChars } = useTextValidation(value, rules)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const borderColor = severity === 'error'
    ? 'border-red-400 dark:border-red-600'
    : severity === 'warning'
      ? 'border-yellow-400 dark:border-yellow-600'
      : 'border-neutral-200 dark:border-neutral-700'

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {t('remake.field.title')}
        </label>
        <div className="flex items-center gap-1.5">
          <button onClick={handleCopy} className="p-1 rounded text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300" title={t('remake.copied')}>
            {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => setEditing(!editing)} className="p-1 rounded text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300">
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {editing ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          autoFocus
          rows={2}
          className={`w-full p-2 text-sm rounded-lg border ${borderColor} bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white resize-none focus:outline-none focus:ring-1 focus:ring-orange-400`}
        />
      ) : (
        <p className={`text-sm p-2 rounded-lg border ${borderColor} bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-white cursor-pointer`} onClick={() => setEditing(true)}>
          {value || original}
        </p>
      )}
      <TitleLengthIndicator chars={charsUsed} max={maxChars} t={t} />
      <ViolationList violations={violations} />
    </div>
  )
}

const MOBILE = 80
const OPTIMAL = 150
const YELLOW = 170
const SCALE = 200

function pct(v: number) {
  return Math.min(Math.max((v / SCALE) * 100, 1), 99)
}

function TitleLengthIndicator({ chars, max, t }: { chars: number; max: number; t: (k: string, o?: Record<string, unknown>) => string }) {
  if (!max) return null
  const r = (k: string) => t(`amazonListing.report.${k}`)
  const clr = chars <= OPTIMAL ? 'text-green-600' : chars <= YELLOW ? 'text-yellow-600' : 'text-red-600'

  return (
    <div className="space-y-1.5">
      <div className="relative h-2.5 mt-5 mb-4">
        <div className="flex h-full rounded-full overflow-hidden">
          <div className="bg-primary-200 dark:bg-primary-900/40" style={{ width: `${pct(MOBILE)}%` }} />
          <div className="bg-green-300 dark:bg-green-800/50" style={{ width: `${pct(OPTIMAL) - pct(MOBILE)}%` }} />
          <div className="bg-yellow-300 dark:bg-yellow-800/50" style={{ width: `${pct(YELLOW) - pct(OPTIMAL)}%` }} />
          <div className="bg-red-300 dark:bg-red-800/40 flex-1" />
        </div>
        <span className="absolute -top-3.5 text-3xs text-primary-600 dark:text-primary-400 -translate-x-1/2" style={{ left: `${pct(MOBILE)}%` }}>{MOBILE}</span>
        <span className="absolute -top-3.5 text-3xs text-green-600 dark:text-green-400 -translate-x-1/2" style={{ left: `${pct(OPTIMAL)}%` }}>{OPTIMAL}</span>
        <div className="absolute top-0 h-full w-0.5 bg-orange-500 rounded-full -translate-x-1/2" style={{ left: `${pct(chars)}%` }} />
        <div className="absolute -translate-x-1/2 flex flex-col items-center" style={{ left: `${pct(chars)}%`, top: '-16px' }}>
          <span className="px-1.5 py-0.5 bg-orange-500 text-white rounded text-2xs font-bold shadow-sm">{chars}</span>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-3xs text-neutral-500 dark:text-neutral-400">
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-primary-200 dark:bg-primary-800" />{r('titleZoneMobile')}</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-300 dark:bg-green-700" />{r('titleZoneOptimal')}</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-yellow-300 dark:bg-yellow-700" />{r('titleZoneLong')}</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-300 dark:bg-red-700" />{r('titleZoneTooLong')}</span>
        </div>
        <span className={`text-xs font-medium ${clr}`}>{chars} / {max} {r('characters')}</span>
      </div>
    </div>
  )
}

function ViolationList({ violations }: { violations: { message: string; severity: string }[] }) {
  if (violations.length === 0) return null
  return (<>{violations.map((v, i) => (
    <p key={i} className={`text-xs ${v.severity === 'error' ? 'text-red-500' : 'text-yellow-600 dark:text-yellow-400'}`}>{v.message}</p>
  ))}</>)
}
